import React, { useState, useRef, useEffect, useContext } from "react";
import { AppContext } from "../context";
import Navbar from "./Navbar";

/**
 * Phone verification by SMS one-time code.
 *
 * A React port of the shared otp-verification.html, wired to the backend
 * instead of the mock: POST /sendOtp texts a 6-digit code, POST /verifyOtp
 * checks it. The code is generated and held server-side, so nothing here can
 * tell you what it is.
 *
 * Renders the phone step, then reveals the code step in place. A correct code
 * calls onVerified() immediately -- no confirmation panel, the parent moves the
 * user straight on.
 */

const RESEND_SECONDS = 30;
const CODE_LENGTH = 6;
const PHONE_LENGTH = 10;

// India only: this page serves the Liberty flow, so the code is fixed rather
// than picked from a list, and PHONE_LENGTH is an Indian mobile number.
const COUNTRY_CODE = "+91";

// Server reasons -> what the user should read. Anything unmapped falls back to
// a generic line rather than surfacing a raw reason code.
const SEND_ERRORS = {
  invalid_phone: "That number doesn't look right. Please check and try again.",
  not_configured: "SMS is not set up yet. Please contact support.",
  send_failed: "We couldn't send the code. Please try again in a moment.",
  ip_rate_limited: "Too many requests from your network. Please try again later.",
  server_error: "Something went wrong. Please try again.",
};

const VERIFY_ERRORS = {
  wrong_code: "That code doesn't look right. Please try again.",
  invalid_code: "Please enter all 6 digits.",
  expired: "That code has expired. Please request a new one.",
  too_many_attempts: "Too many wrong attempts. Please request a new code.",
  ip_rate_limited: "Too many attempts from your network. Please try again later.",
  server_error: "Something went wrong. Please try again.",
};

// Same primary colour every other button in the app uses. The navy is only a
// fallback for a client config that carries no colours -- it is the shade the
// page was designed in.
const DEFAULT_ACCENT = "#1c1550";

/**
 * Digits only, at most PHONE_LENGTH. A pasted or autofilled number that still
 * carries the country code ("+91 98765 43210") or a trunk 0 loses it first, so
 * the real last digits aren't the ones cut off. Only when exactly that prefix
 * is extra: an 11th digit typed onto a number that starts "91" is just dropped.
 */
const toNationalNumber = (raw, countryCode) => {
  let digits = raw.replace(/\D/g, "");
  const cc = countryCode.replace(/\D/g, "");
  if (digits.length === cc.length + PHONE_LENGTH && digits.startsWith(cc))
    digits = digits.slice(cc.length);
  else if (digits.length === PHONE_LENGTH + 1 && digits.startsWith("0"))
    digits = digits.slice(1);
  return digits.slice(0, PHONE_LENGTH);
};

/**
 * The back arrow sits on the client's navbar colour, so take dark or light ink
 * by its brightness. Anything that isn't a hex colour gets white, which suits
 * the app's default dark navbar.
 */
const inkOn = (color) => {
  const match = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color || "");
  if (!match) return "#ffffff";
  let hex = match[1];
  if (hex.length === 3)
    hex = hex
      .split("")
      .map((c) => c + c)
      .join("");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return 0.299 * r + 0.587 * g + 0.114 * b > 150 ? "#1a1a1a" : "#ffffff";
};

const OtpVerification = ({ onVerified, onBack }) => {
  const { inspectionId, config } = useContext(AppContext);

  const accent =
    (config && config["colors"] && config["colors"]["btn"]) || DEFAULT_ACCENT;
  const backInk = inkOn(config && config["colors"] && config["colors"]["nav"]);

  const countryCode = COUNTRY_CODE;
  const [phone, setPhone] = useState("");
  const [phoneLocked, setPhoneLocked] = useState(false);
  const [phoneError, setPhoneError] = useState("");

  const [digits, setDigits] = useState(Array(CODE_LENGTH).fill(""));
  const [codeError, setCodeError] = useState("");
  const [shake, setShake] = useState(false);

  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  // Bumped on every code sent, so the SMS reader restarts for a resent code.
  const [codeRequests, setCodeRequests] = useState(0);

  const boxRefs = useRef([]);
  const timerRef = useRef(null);

  const phoneValid = phone.length === PHONE_LENGTH;
  const codeComplete = digits.every((d) => d !== "");

  // --- resend countdown ---------------------------------------------------
  const startTimer = (seconds = RESEND_SECONDS) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setSecondsLeft(seconds);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  // Clear the interval on unmount so it cannot tick into a dead component.
  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
    },
    []
  );

  const clearCode = () => {
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError("");
  };

  // --- send / resend ------------------------------------------------------
  const requestCode = async () => {
    if (sending) return;
    setSending(true);
    setPhoneError("");
    try {
      const res = await fetch("/sendOtp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ countryCode, phoneNumber: phone }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        // A cooldown is not really an error: the previous code is still live, so
        // just show the timer again rather than blocking the user.
        if (data.reason === "cooldown") {
          setPhoneLocked(true);
          startTimer(data.retryAfterSeconds || RESEND_SECONDS);
          return;
        }
        setPhoneError(SEND_ERRORS[data.reason] || SEND_ERRORS.server_error);
        return;
      }

      setPhoneLocked(true);
      clearCode();
      setCodeRequests((n) => n + 1);
      startTimer(data.resendAfterSeconds || RESEND_SECONDS);
      // Focus the first box once it has rendered.
      setTimeout(() => boxRefs.current[0] && boxRefs.current[0].focus(), 0);
    } catch (e) {
      setPhoneError(SEND_ERRORS.server_error);
    } finally {
      setSending(false);
    }
  };

  const changeNumber = () => {
    setPhoneLocked(false);
    clearCode();
    if (timerRef.current) clearInterval(timerRef.current);
    setSecondsLeft(0);
  };

  // --- verify -------------------------------------------------------------
  const failCode = (message) => {
    setDigits(Array(CODE_LENGTH).fill(""));
    setCodeError(message);
    setShake(true);
    setTimeout(() => setShake(false), 400);
    if (boxRefs.current[0]) boxRefs.current[0].focus();
  };

  const submitCode = async () => {
    if (verifying || !codeComplete) return;
    setVerifying(true);
    try {
      const res = await fetch("/verifyOtp", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          countryCode,
          phoneNumber: phone,
          code: digits.join(""),
          inspectionId,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.verified) {
        let message = VERIFY_ERRORS[data.reason] || VERIFY_ERRORS.server_error;
        if (data.reason === "wrong_code" && data.attemptsLeft)
          message += ` ${data.attemptsLeft} attempt${
            data.attemptsLeft === 1 ? "" : "s"
          } left.`;
        failCode(message);
        return;
      }

      // Straight through to the next page: no confirmation panel, no delay. The
      // parent swaps this component out, so there is no success state to render.
      if (timerRef.current) clearInterval(timerRef.current);
      if (onVerified) onVerified();
    } catch (e) {
      failCode(VERIFY_ERRORS.server_error);
    } finally {
      setVerifying(false);
    }
  };

  // --- code box behaviour -------------------------------------------------
  const setDigitAt = (index, value) => {
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // The whole code at once -- pasted, read from the SMS, or inserted by the
  // keyboard's autofill suggestion -- always lands from the first box.
  const fillCode = (list) => {
    const next = Array(CODE_LENGTH).fill("");
    list.slice(0, CODE_LENGTH).forEach((d, i) => {
      next[i] = d;
    });
    setDigits(next);
    setCodeError("");
    const last = Math.min(list.length, CODE_LENGTH) - 1;
    if (boxRefs.current[last]) boxRefs.current[last].focus();
  };

  const onDigitInput = (index, raw) => {
    const typed = raw.replace(/\D/g, "");
    // More digits than one keystroke can make is the code arriving whole: iOS
    // and Android keyboards insert their SMS suggestion into the focused box.
    if (typed.length > 2 || (typed.length === 2 && !digits[index])) {
      fillCode(typed.split(""));
      return;
    }
    // Two digits in a filled box: keep the one just typed, wherever the cursor was.
    const value =
      typed.length === 2
        ? typed[0] === digits[index]
          ? typed[1]
          : typed[0]
        : typed;
    setDigitAt(index, value);
    setCodeError("");
    const next = boxRefs.current[index + 1];
    if (value && index < CODE_LENGTH - 1 && next) next.focus();
  };

  const onDigitKeyDown = (index, e) => {
    const prev = boxRefs.current[index - 1];
    if (e.key === "Backspace" && !digits[index] && index > 0 && prev)
      prev.focus();
  };

  // Let the whole code be pasted, or arrive from an SMS autofill suggestion.
  const onDigitPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData("text").match(/\d/g) || []).slice(
      0,
      CODE_LENGTH
    );
    if (!pasted.length) return;
    fillCode(pasted);
  };

  const showCodeStep = phoneLocked;

  // Chrome on Android reads the code straight from the SMS (Web OTP API); the
  // backend ends the message with "@<host> #<code>" for this. The user allows it
  // once and the boxes fill. Other browsers fill the boxes from the keyboard's
  // one-time-code suggestion via onDigitInput.
  useEffect(() => {
    if (!showCodeStep || !("OTPCredential" in window)) return undefined;
    const ac = new AbortController();
    navigator.credentials
      .get({ otp: { transport: ["sms"] }, signal: ac.signal })
      .then((otp) => {
        const code = (otp && otp.code && otp.code.match(/\d/g)) || [];
        if (code.length) fillCode(code);
      })
      // Aborted, dismissed or timed out: typing the code still works.
      .catch(() => {});
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCodeStep, codeRequests]);

  // Verify as soon as the sixth digit lands -- typed, pasted, autofilled or read
  // from the SMS -- so nobody has to press the button. Any failure clears the
  // boxes (failCode), so this can't fire again until a new code is complete.
  // The button stays, and shows "Verifying…" while the check runs.
  useEffect(() => {
    if (codeComplete) submitCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digits]);

  // One variable drives the button, the focus rings and the links, so a client's
  // primary colour styles the whole page.
  return (
    <div className="otp-page" style={{ "--otp-accent": accent }}>
      {/* The app's own navbar, same as every other screen, so the client's logo
          and nav colour carry over instead of a second brand treatment here. */}
      <Navbar />
      {onBack && (
        <button
          type="button"
          className="otp-back-btn"
          aria-label="Back"
          style={{ color: backInk }}
          onClick={onBack}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      <div className="otp-card">
        <div className="otp-content-wrap">
          <div className="otp-icon-badge">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="6" y="2" width="12" height="20" rx="2.5" />
              <path d="M11 18h2" />
            </svg>
          </div>

          <h1 className="otp-title">Verify your number</h1>
          <p className="otp-subtext">
            We&apos;ll text a {CODE_LENGTH}-digit code to confirm it&apos;s you.
          </p>

          {/* Phone entry */}
          <div className="otp-phone-section">
              <div className={`otp-phone-row ${phoneLocked ? "otp-locked" : ""}`}>
                <span className="otp-country-code">🇮🇳 {countryCode}</span>
                {/* No maxLength: it would truncate a pasted "+91 98765 43210"
                    before onChange could strip the prefix. The 10-digit cap is
                    applied in toNationalNumber instead. */}
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  placeholder={`Enter ${PHONE_LENGTH}-digit mobile number`}
                  value={phone}
                  disabled={phoneLocked}
                  onChange={(e) => {
                    setPhone(toNationalNumber(e.target.value, countryCode));
                    setPhoneError("");
                  }}
                />
                {phoneLocked && (
                  <button className="otp-change-link" onClick={changeNumber}>
                    Change
                  </button>
                )}
              </div>
              <div className="otp-field-error">{phoneError}</div>
              {!showCodeStep && (
                <p className="otp-fine-print">
                  By continuing, you agree to receive a one-time SMS code for
                  verification. Message &amp; data rates may apply.
                </p>
              )}
          </div>

          {/* Code entry, revealed in place */}
          {showCodeStep && (
            <div className="otp-code-section">
              <p className="otp-subtext otp-code-subtext">
                Enter the {CODE_LENGTH}-digit code we just sent you
              </p>

              <div
                className={`otp-row ${codeError ? "otp-error-state" : ""} ${
                  shake ? "otp-shake" : ""
                }`}
              >
                {digits.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => {
                      boxRefs.current[i] = el;
                    }}
                    className={`otp-box ${d ? "otp-filled" : ""}`}
                    type="tel"
                    inputMode="numeric"
                    // No maxLength: it cuts an autofilled code down to its first
                    // digit. onDigitInput keeps each box to one digit.
                    autoComplete={i === 0 ? "one-time-code" : "off"}
                    value={d}
                    onChange={(e) => onDigitInput(i, e.target.value)}
                    onKeyDown={(e) => onDigitKeyDown(i, e)}
                    onPaste={onDigitPaste}
                  />
                ))}
              </div>
              {codeError && <p className="otp-code-error">{codeError}</p>}
            </div>
          )}
        </div>

        {/* Pinned to the bottom of the card, outside the scrolling body, so the
            action sits in the same place on both steps and stays reachable when
            the keyboard is up. */}
        <div className="otp-footer">
          {showCodeStep ? (
            <>
              <button
                className={`otp-primary-btn ${
                  codeComplete && !verifying ? "otp-enabled" : ""
                }`}
                disabled={!codeComplete || verifying}
                onClick={submitCode}
              >
                {verifying ? "Verifying…" : "Verify & Continue"}
              </button>

              <div className="otp-resend-row">
                {secondsLeft > 0 ? (
                  <span>
                    Resend code in 0:
                    {String(secondsLeft).padStart(2, "0")}
                  </span>
                ) : (
                  <button
                    className="otp-resend-link"
                    disabled={sending}
                    onClick={() => requestCode(true)}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </>
          ) : (
            <button
              className={`otp-primary-btn ${
                phoneValid && !sending ? "otp-enabled" : ""
              }`}
              disabled={!phoneValid || sending}
              onClick={() => requestCode(false)}
            >
              {sending ? "Sending…" : "Send OTP"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
